// Port EXACTO de las funciones de color validadas en el preview aprobado por
// Juan (2026-07-09, artifact-home-clean.html líneas 952-988): a partir del
// color pleno de un cliente (el mismo que pinta la placa del CC en variante
// C), calcula un acento DESATURADO para usarlo en puntitos/swatches donde el
// color pleno competiría demasiado (Referencias al pie de Obras). No tocar
// la fórmula sin repasar el preview — quedó cerrada tal cual está.
//
// Receta: se "apaga" el color un 28% hacia el gris de su propia luminancia;
// si el resultado queda muy pálido se oscurece un 45% en tema claro, y si
// queda muy oscuro se aclara un 42% en tema oscuro.

export interface AccentVariants {
  /** Variante para tema claro. */
  l: string;
  /** Variante para tema oscuro. */
  d: string;
}

type Rgb = [number, number, number];

function hexRgb(hex: string): Rgb {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function lum(rgb: Rgb): number {
  return (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
}

function mix(a: Rgb, b: Rgb, k: number): Rgb {
  return [
    Math.round(a[0] + (b[0] - a[0]) * k),
    Math.round(a[1] + (b[1] - a[1]) * k),
    Math.round(a[2] + (b[2] - a[2]) * k),
  ];
}

function css(rgb: Rgb): string {
  return `rgb(${rgb.join(',')})`;
}

export function accentVariants(hex: string | null | undefined): AccentVariants | null {
  if (!hex) return null;
  let rgb: Rgb;
  try {
    rgb = hexRgb(hex);
  } catch {
    return null;
  }
  const L = lum(rgb);
  const gray: Rgb = [Math.round(L * 255), Math.round(L * 255), Math.round(L * 255)];
  const soft = mix(rgb, gray, 0.28); // "apagar un poquito"
  const light = L > 0.68 ? mix(soft, [0, 0, 0], 0.45) : soft; // pálido → oscurecer en light
  const dark = L < 0.25 ? mix(soft, [255, 255, 255], 0.42) : soft; // muy oscuro → aclarar en dark
  return { l: css(light), d: css(dark) };
}

// Sufijo societario al FINAL del nombre, como palabra aparte (precedido de
// espacio y opcionalmente de coma): S.A., S.A.S., S.R.L. y UTE, con o sin
// puntos y en cualquier caja («Privera S.A.», «Centro Construcciones SA»,
// «Ocean Export, S.R.L.»). BACKLOG «Chicos… (b)», H58.
const SUFIJO_SOCIETARIO = /,?\s+(?:s\.?a\.?(?:s\.?)?|s\.?r\.?l\.?|u\.?t\.?e\.?)$/i;

// Monograma del cliente para el chip sin logo (2 letras: primera + última
// palabra del nombre, o las 2 primeras letras si es una sola palabra). Antes
// de partir se quitan los sufijos societarios del final (de a uno, por si
// vienen encadenados), salvo que el nombre sea solo el sufijo: nunca se vacía.
// Con un nombre vacío o de solo espacios devuelve ''.
export function monogram(cliente: string): string {
  let nombre = cliente.trim();
  if (!nombre) return '';
  for (let m = SUFIJO_SOCIETARIO.exec(nombre); m && m.index > 0; m = SUFIJO_SOCIETARIO.exec(nombre)) {
    nombre = nombre.slice(0, m.index);
  }
  // Palabras con al menos una letra o número: un guion suelto («Obra - UTE») no es
  // una palabra del monograma. Si no queda ninguna, se usan todas (nunca vacío).
  const todas = nombre.split(/\s+/).filter(Boolean);
  const conLetras = todas.filter(w => /[\p{L}\p{N}]/u.test(w));
  const words = conLetras.length > 0 ? conLetras : todas;
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

// Iniciales de la encargada para su avatar circular (ej. "A. Vera" -> "AV").
export function encInitials(enc: string): string {
  return enc
    .split(/[\s.]+/)
    .filter(Boolean)
    .map(w => w[0].toUpperCase())
    .join('');
}

// Rango Unicode de las marcas diacríticas combinantes que deja el NFD
// (acentos, tildes, diéresis sueltos de su letra base): U+0300 a U+036F. En
// el script original del preview esto se filtraba con un regex que tenía
// esos dos codepoints pegados literalmente adentro del `[...]` (caracteres
// invisibles en el fuente, el motivo del "cuidado" de la spec) — acá se
// compara por código numérico en vez de meter caracteres combinantes
// sueltos en el archivo, mismo rango exacto y sin nada invisible.
const COMBINING_DIACRITICS_START = 0x0300;
const COMBINING_DIACRITICS_END = 0x036f;

// Normaliza texto para comparar/filtrar sin distinguir mayúsculas ni
// acentos (misma receta validada en el preview, 2026-07-09): descompone en
// NFD y descarta las marcas combinantes del rango de arriba.
export function normText(s: string | null | undefined): string {
  const decomposed = (s || '').toLowerCase().normalize('NFD');
  let out = '';
  for (const ch of decomposed) {
    const code = ch.codePointAt(0) ?? 0;
    if (code < COMBINING_DIACRITICS_START || code > COMBINING_DIACRITICS_END) out += ch;
  }
  return out;
}
